import { useMutation } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import { api } from '../lib/api.js';

/**
 * Trockenlauf.
 *
 * Beantwortet die Frage, die vor dem Scharfschalten im Raum steht: was
 * passiert mit einem Kontakt, und wann? Das Diagramm zeigt Kästen und
 * Pfeile, aber nicht, dass dieser Ablauf zwölf Tage läuft und im
 * ungünstigsten Fall drei Mails verschickt.
 *
 * Es wird nichts eingereiht und nichts verschickt — reine Rechnung.
 */

interface SimStep {
  nodeId: string;
  type: string;
  label: string;
  description: string;
  offsetMs: number;
  offsetLabel: string;
  viaBranch: string | null;
}

interface SimPath {
  decisions: Array<{ nodeId: string; branch: string }>;
  steps: SimStep[];
  totalMs: number;
  totalLabel: string;
  emails: number;
  outcome: 'ende' | 'ausstieg' | 'abgeschnitten';
}

interface SimResult {
  problems: Array<{ nodeId?: string; message: string }>;
  paths: SimPath[];
  longestMs: number;
  longestLabel: string;
  maxEmails: number;
}

const OUTCOME_LABELS: Record<string, string> = {
  ende: 'Ablauf endet',
  ausstieg: 'Ausstieg',
  abgeschnitten: 'abgeschnitten (zu viele Schritte)',
};

export function JourneySimulation({ journeyId }: { journeyId: string }) {
  const run = useMutation({
    mutationFn: () => api.post<SimResult>(`/api/journeys/${journeyId}/simulate`, {}),
  });

  return (
    <section className="card">
      <div className="card__body stack">
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div>
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Trockenlauf</h2>
            <p className="muted text-sm">
              Spielt jeden möglichen Weg durch — ohne etwas zu verschicken.
            </p>
          </div>
          <Button variant="secondary" onClick={() => run.mutate()} loading={run.isPending}>
            Durchspielen
          </Button>
        </div>

        {run.isError && (
          <div className="alert" data-tone="danger" role="alert">
            {run.error instanceof Error ? run.error.message : 'Trockenlauf fehlgeschlagen.'}
          </div>
        )}

        {run.data && (
          <>
            {run.data.problems.length > 0 && (
              <div className="alert" data-tone="warning" role="alert">
                <strong>Der Ablauf lässt sich so nicht scharf schalten:</strong>
                <ul className="check__advice text-sm">
                  {run.data.problems.map((problem, index) => (
                    <li key={index}>
                      {problem.nodeId ? `${problem.nodeId}: ` : ''}
                      {problem.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="stat-grid">
              <div className="stat">
                <span className="stat__label">Läuft längstens</span>
                <span className="stat__value">{run.data.longestLabel}</span>
              </div>
              <div className="stat">
                <span className="stat__label">Mails im Höchstfall</span>
                <span className="stat__value" data-numeric>
                  {run.data.maxEmails}
                </span>
                <span className="stat__note">je Kontakt</span>
              </div>
              <div className="stat">
                <span className="stat__label">Mögliche Wege</span>
                <span className="stat__value" data-numeric>
                  {run.data.paths.length}
                </span>
              </div>
            </div>

            {run.data.paths.map((path, index) => (
              <details key={index} className="sim-path" open={run.data!.paths.length <= 2}>
                <summary>
                  Weg {index + 1}
                  {path.decisions.length > 0 && (
                    <span className="muted text-sm">
                      {' '}
                      (
                      {path.decisions
                        .map((d) => `${d.nodeId}: ${d.branch === 'yes' ? 'ja' : d.branch}`)
                        .join(', ')}
                      )
                    </span>
                  )}
                  <span className="sim-path__meta">
                    {path.totalLabel} · {path.emails} {path.emails === 1 ? 'Mail' : 'Mails'} ·{' '}
                    {OUTCOME_LABELS[path.outcome]}
                  </span>
                </summary>

                <ol className="timeline" style={{ marginTop: 'var(--space-3)' }}>
                  {path.steps.map((step, stepIndex) => (
                    <li key={`${step.nodeId}-${stepIndex}`} data-kind="activity">
                      <strong>{step.label}</strong>
                      <div className="text-sm">{step.description}</div>
                      <span className="timeline__when">
                        {step.offsetMs === 0 ? 'sofort' : `nach ${step.offsetLabel}`}
                      </span>
                    </li>
                  ))}
                </ol>
              </details>
            ))}
          </>
        )}
      </div>
    </section>
  );
}
